/**
 * Select Component - Reusable select dropdown
 *
 * Variants:
 * - default: Standard select
 * - filled: Filled background
 * - outline: Outlined select
 *
 * Sizes:
 * - sm: Small
 * - md: Medium (default)
 * - lg: Large
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

const selectVariants = cva(
  'flex w-full rounded-md border bg-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none',
  {
    variants: {
      variant: {
        default: 'border-neutral-300 focus-visible:border-primary-500 focus-visible:ring-primary-500',
        filled: 'border-transparent bg-neutral-100 focus-visible:border-primary-500 focus-visible:ring-primary-500 focus-visible:bg-white',
        outline: 'border-2 border-neutral-300 bg-transparent focus-visible:border-primary-500 focus-visible:ring-primary-500',
      },
      size: {
        sm: 'h-8 px-3 py-1 text-xs',
        md: 'h-9 px-3 py-1 text-sm',
        lg: 'h-10 px-4 py-2 text-base',
      },
      state: {
        default: '',
        error: 'border-error-500 focus-visible:border-error-500 focus-visible:ring-error-500',
        success: 'border-success-500 focus-visible:border-success-500 focus-visible:ring-success-500',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      state: 'default',
    },
  }
);

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    VariantProps<typeof selectVariants> {
  label?: string;
  error?: string;
  helperText?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
  containerClassName?: string;
}

/**
 * Select Component
 *
 * @example
 * ```tsx
 * <Select
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' },
 *   ]}
 * />
 * <Select label="Category" options={categories} />
 * <Select error="Required" />
 * ```
 */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, variant, size, state, label, error, helperText, options, placeholder, containerClassName, id, ...props }, ref) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorMessage = error || (state === 'error' ? 'This field has an error' : '');
    const hasError = !!errorMessage;

    return (
      <div className={cn('w-full', containerClassName)}>
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'mb-1.5 block text-sm font-medium leading-6',
              hasError ? 'text-error-500' : 'text-neutral-900'
            )}
          >
            {label}
          </label>
        )}

        <div className="relative">
          <select
            id={selectId}
            ref={ref}
            className={cn(selectVariants({ variant, size, state: hasError ? 'error' : state }), 'pr-10', className)}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>

          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-neutral-400" />
        </div>

        {hasError && (
          <p id={`${selectId}-error`} className="mt-1.5 text-sm text-error-500">
            {errorMessage}
          </p>
        )}

        {helperText && !hasError && (
          <p id={`${selectId}-helper`} className="mt-1.5 text-sm text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
